import { useState, useEffect, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import HiddenJHReport from "./HiddenJHReport";
import Acumen_25_Years_LOGO from "../assets/Acumen_25_Years_LOGO.png";

// ─── DESIGN TOKENS ────────────────────────────────────────────────────────────
const T = {
  navy: "#A81026",
  blue: "#C4163A",
  teal: "#7B0F2B",
  gold: "#D79A1E",
  slate: "#5B677A",
  bg: "#F5F8FC",
  card: "#FFFFFF",
  line: "#DCE5EF",
  ink: "#1D2733",
};

// Quadrant accent colors (mirrors PROFILE_COLOR on page 3)
const Q = {
  arena: "#1f8f55",
  blind: "#6d28d9",
  concealed: "#1d4ed8",
  discover: "#d88a1f",
};
const NEUTRAL = "#9AA5B1";

// ── Profile → color map (from GlobalJHResponses) ──────────────────────────────
const PROFILE_COLOR = {
  arena:   { bg: "#dcfce7", color: "#1f8f55", border: "#bbf7d0" }, // green
  driver:  { bg: "#ede9fe", color: "#6d28d9", border: "#ddd6fe" }, // purple
  advisor: { bg: "#dbeafe", color: "#1d4ed8", border: "#bfdbfe" }, // blue
  guarded: { bg: "#fef3c7", color: "#d88a1f", border: "#fde68a" }, // amber
};

// ─── STATIC PROFILE DEFINITIONS ───────────────────────────────────────────────
const PROFILE_DEFINITIONS = [
  {
    key: "arena",
    label: "Arena",
    tagline: '"Open & trusted communicator"',
    thresholds: "High Exposure · High Feedback",
    overview:
      "You display high levels of transparency and receptivity. You are a natural trust-builder who fosters psychological safety within your team. Your openness to both sharing and receiving creates an environment where honest dialogue flourishes.",
    strategy:
      "Focus on mentoring team members to model your communication style. Help others feel safe enough to expand their own Open Arena.",
    strengths:
      "Builds psychological safety, accelerates trust, reduces misunderstanding, and enables high-performing collaboration.",
    watchOut:
      "Over-disclosure or unsolicited feedback can sometimes feel intrusive — calibrate openness to the relationship and context.",
  },
  {
    key: "driver",
    label: "Blind",
    tagline: '"Visionary but occasionally unilateral"',
    thresholds: "High Exposure · Low/Avg Feedback",
    overview:
      "You are proactive in sharing your vision and perspective, but may be inadvertently minimizing team input. You tend to lead from the front with confidence, which can be inspiring, but the loop back from others is underutilised.",
    strategy:
      "Adopt a 'Feedback-First' protocol. Always solicit three distinct perspectives before stating your final position in key discussions.",
    strengths:
      "Clear communicator, decisive, confident in sharing views, sets direction for others.",
    watchOut:
      "Can create one-directional conversations; team members may feel unheard or hesitant to offer candid pushback.",
  },
  {
    key: "advisor",
    label: "Concealed",
    tagline: '"Excellent listener, careful discloser"',
    thresholds: "Low/Avg Exposure · High Feedback",
    overview:
      "You excel at listening and drawing out the perspectives of others, but may be holding back your own expertise or reasoning. Your team benefits from your attentiveness but may lack visibility into your rationale.",
    strategy:
      "Practice 'Intentional Disclosure'—your team needs to understand your thinking to fully align with your goals. Share the 'why' behind your decisions more often.",
    strengths:
      "Empathetic listener, skilled at gathering information, makes others feel heard and valued.",
    watchOut:
      "Withholding your perspective can create ambiguity about your position and reduce your perceived decisiveness.",
  },
  {
    key: "guarded",
    label: "Discover",
    tagline: '"Private and self-contained"',
    thresholds: "Low Exposure · Low Feedback",
    overview:
      "You maintain a professional and reserved style. This provides privacy and composure but may limit deep team collaboration and reduce the trust signals that enable high-performing teams.",
    strategy:
      "Small, consistent, low-stakes disclosures will build the rapport necessary to solicit more meaningful feedback over time. Start with sharing context on decisions you have already made.",
    strengths:
      "Composed, careful, considered — rarely caught off-guard; brings stability to uncertain situations.",
    watchOut:
      "Low transparency can create speculation or mistrust; others may struggle to read your intentions or feel excluded from your thinking.",
  },
];

// ─── DIMENSION DEFINITIONS ────────────────────────────────────────────────────
const DIMENSION_DEFINITIONS = [
  {
    key: "exposure",
    label: "Exposure (Disclosure)",
    shortLabel: "Exposure",
    desc: "Your willingness to share your own thoughts, feelings, rationale, and perspective with others. High exposure indicates that you communicate openly and proactively, reducing the size of your Concealed quadrant.",
  },
  {
    key: "feedback",
    label: "Feedback Receptivity",
    shortLabel: "Feedback",
    desc: "Your openness to receiving and actively soliciting perspectives, observations, and input from others. High feedback receptivity reduces your Blind quadrant by bringing in information you may not have about yourself.",
  },
];

// ─── HELPERS ──────────────────────────────────────────────────────────────────
function scoreBand(val) {
  if (val >= 7) return "High";
  if (val >= 4) return "Average";
  return "Low";
}

function deriveProfile(exposure, feedback) {
  const eBand = scoreBand(exposure);
  const fBand = scoreBand(feedback);
  if (eBand === "High" && fBand === "High") return "arena";
  if (eBand === "High") return "driver";
  if (fBand === "High") return "advisor";
  return "guarded";
}

function formatDate(date) {
  return date.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
}

// ─── JOHARI WINDOW CHART ──────────────────────────────────────────────────────
function JohariWindowChart({ exposure, feedback, loading, activeLabel }) {
  const size = 300;
  const pad = { top: 48, right: 56, bottom: 36, left: 56 };
  const cW = size - pad.left - pad.right;
  const cH = size - pad.top - pad.bottom;

  const rawExp = loading ? 5 : (exposure ?? 5);
  const rawFb = loading ? 5 : (feedback ?? 5);

  const xSplit = (rawFb / 10) * cW;
  const ySplit = (rawExp / 10) * cH;

  const quads = [
    { x: 0, y: 0, w: xSplit, h: ySplit, fill: Q.arena, alpha: ".18", label: "ARENA", sub: "Open" },
    { x: xSplit, y: 0, w: cW - xSplit, h: ySplit, fill: Q.blind, alpha: ".14", label: "BLIND", sub: "Unknown to self" },
    { x: 0, y: ySplit, w: xSplit, h: cH - ySplit, fill: Q.concealed, alpha: ".12", label: "CONCEALED", sub: "Hidden" },
    { x: xSplit, y: ySplit, w: cW - xSplit, h: cH - ySplit, fill: Q.discover, alpha: ".10", label: "DISCOVER", sub: "Unknown" },
  ].map(q => q.label === activeLabel
    ? q
    : { ...q, fill: NEUTRAL, alpha: ".12" });

  const labelCenter = (q) => ({ cx: q.x + q.w / 2, cy: q.y + q.h / 2 });

  return (
    <svg viewBox={`0 0 ${size} ${size}`} width="100%" style={{ maxWidth: size, display: "block", margin: "0 auto" }}>
      <text x={pad.left + xSplit / 2} y={18} textAnchor="middle" fontSize="8" fontWeight="700" fill={T.slate} letterSpacing=".06em">KNOWN TO SELF</text>
      <text x={pad.left + xSplit + (cW - xSplit) / 2} y={18} textAnchor="middle" fontSize="8" fontWeight="700" fill={T.slate} letterSpacing=".06em">UNKNOWN TO SELF</text>
      <text x={14} y={pad.top + ySplit / 2} textAnchor="middle" fontSize="8" fontWeight="700" fill={T.slate} letterSpacing=".06em"
        transform={`rotate(-90, 14, ${pad.top + ySplit / 2})`}>KNOWN TO OTHERS</text>
      <text x={14} y={pad.top + ySplit + (cH - ySplit) / 2} textAnchor="middle" fontSize="8" fontWeight="700" fill={T.slate} letterSpacing=".06em"
        transform={`rotate(-90, 14, ${pad.top + ySplit + (cH - ySplit) / 2})`}>UNKNOWN TO OTHERS</text>
      <text x={size - 10} y={pad.top + cH / 2} textAnchor="middle" fontSize="8" fontWeight="700" fill={T.navy} letterSpacing=".05em"
        transform={`rotate(90, ${size - 10}, ${pad.top + cH / 2})`}>Disclosure →</text>
      <text x={pad.left + cW / 2} y={size - 6} textAnchor="middle" fontSize="8" fontWeight="700" fill={T.navy} letterSpacing=".05em">Feedback →</text>

      <g transform={`translate(${pad.left},${pad.top})`}>
        {quads.map((q, i) => (
          <rect key={i} x={q.x} y={q.y} width={q.w} height={q.h}
            fill={q.fill} fillOpacity={loading ? ".05" : q.alpha} rx={4} />
        ))}
        <rect x={0} y={0} width={cW} height={cH} fill="none" stroke={T.line} strokeWidth={1.5} rx={4} />
        {!loading && (
          <>
            <line x1={xSplit} y1={0} x2={xSplit} y2={cH} stroke={T.navy} strokeWidth={1.5} strokeDasharray="4 3" />
            <line x1={0} y1={ySplit} x2={cW} y2={ySplit} stroke={T.navy} strokeWidth={1.5} strokeDasharray="4 3" />
            <rect x={xSplit - 16} y={cH - 14} width={32} height={14} rx={4} fill={T.navy} />
            <text x={xSplit} y={cH - 4} textAnchor="middle" fontSize="9" fontWeight="700" fill="#fff">F:{rawFb}</text>
            <rect x={cW - 26} y={ySplit - 7} width={28} height={14} rx={4} fill={T.navy} />
            <text x={cW - 12} y={ySplit + 3} textAnchor="middle" fontSize="9" fontWeight="700" fill="#fff">E:{rawExp}</text>
          </>
        )}
        {quads.map((q, i) => {
          if (q.w < 10 || q.h < 10) return null;
          const { cx, cy } = labelCenter(q);
          return (
            <g key={i}>
              <text x={cx} y={cy - 7} textAnchor="middle" fontSize="9" fontWeight="800" fill={q.fill} fillOpacity={loading ? ".3" : "1"} letterSpacing=".08em">{q.label}</text>
              {q.h > 30 && (
                <text x={cx} y={cy + 6} textAnchor="middle" fontSize="8" fill={T.slate} fillOpacity={loading ? ".3" : ".8"}>{q.sub}</text>
              )}
            </g>
          );
        })}
        {loading && <rect x={0} y={0} width={cW} height={cH} rx={4} fill="#EAF0F6" fillOpacity={0.5} />}
      </g>
    </svg>
  );
}

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
export default function JHResult() {
  const location = useLocation();
  const navState = location.state || {};

  const [reportMeta, setReportMeta] = useState(null);
  const [scores, setScores] = useState(null);
  const [activeProfile, setActiveProfile] = useState(null);
  const [insightText, setInsightText] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pdfData, setPdfData] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [submissionTime, setSubmissionTime] = useState(null);
  const [openProfile, setOpenProfile] = useState(null);
  const generatingRef = useRef(false);

  // ── PDF handlers ───────────────────────────────────────────────────────────
  const handleDownloadPDF = () => {
    if (generatingRef.current) return;
    generatingRef.current = true;
    setIsGenerating(true);
    setPdfData({
      userInfo: { name: reportMeta?.orgName || "Participant" },
      scores: scores || {},
      activeProfile,
      insightText: insightText || "",
      submission_time: submissionTime,
    });
  };

  const handlePdfComplete = useCallback(() => {
    generatingRef.current = false;
    setIsGenerating(false);
    setPdfData(null);
  }, []);

  // ── Data fetching ──────────────────────────────────────────────────────────
  useEffect(() => {
    async function loadData() {
      try {
        setLoading(true);

        if (navState?.scores) {
          const jhData = navState.scores;
          const exposure = jhData.exposure?.normalized ?? jhData.exposure ?? 0;
          const feedback = jhData.feedback?.normalized ?? jhData.feedback ?? 0;
          const exposureNum = Number(exposure) || 0;
          const feedbackNum = Number(feedback) || 0;
          const profile = deriveProfile(exposureNum, feedbackNum);
          const profDef = PROFILE_DEFINITIONS.find(p => p.key === profile);
          const eBand = scoreBand(exposureNum);
          const fBand = scoreBand(feedbackNum);

          const dateStr = jhData.submission_time
            ? formatDate(new Date(jhData.submission_time))
            : formatDate(new Date());

          setReportMeta({
            orgName: navState.name || "Participant",
            period: dateStr,
            reportTitle: "Johari Window Assessment",
          });
          setScores({ exposure: exposureNum, feedback: feedbackNum });
          setActiveProfile(profile);
          setInsightText(`Your Exposure score of ${exposureNum}/10 (${eBand}) and Feedback score of ${feedbackNum}/10 (${fBand}) place you in the ${profDef?.label ?? profile} profile. ${profDef?.strategy ?? ""}`);
          setSubmissionTime(jhData.submission_time ?? new Date().toISOString());
          return;
        }

        const email = navState?.email ?? null;
        const phone = navState?.phone ?? null;

        if (!email && !phone) {
          setError("No result data found. Please complete the survey first.");
          return;
        }

        const API_BASE = import.meta.env.VITE_API_URL ?? "";
        const res = await fetch(`${API_BASE}/api/calculate-score/jh`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ survey_id: 31, email, phone }),
        });

        if (!res.ok) {
          const errBody = await res.json().catch(() => ({}));
          throw new Error(errBody.error ?? `Server error ${res.status}`);
        }

        const json = await res.json();
        if (!json.success) throw new Error(json.error ?? "Unexpected response");

        const jhData = json.data;
        const exposure = jhData.exposure?.normalized ?? 0;
        const feedback = jhData.feedback?.normalized ?? 0;
        const exposureNum = Number(exposure) || 0;
        const feedbackNum = Number(feedback) || 0;
        const profile = deriveProfile(exposureNum, feedbackNum);
        const profDef = PROFILE_DEFINITIONS.find(p => p.key === profile);
        const eBand = scoreBand(exposureNum);
        const fBand = scoreBand(feedbackNum);

        const dateStr = jhData.submission_time
          ? formatDate(new Date(jhData.submission_time))
          : formatDate(new Date());

        setReportMeta({ orgName: jhData.name || "Participant", period: dateStr, reportTitle: "Johari Window Assessment" });
        setScores({ exposure: exposureNum, feedback: feedbackNum });
        setActiveProfile(profile);
        setInsightText(`Your Exposure score of ${exposureNum}/10 (${eBand}) and Feedback score of ${feedbackNum}/10 (${fBand}) place you in the ${profDef?.label ?? profile} profile. ${profDef?.strategy ?? ""}`);
        setSubmissionTime(jhData.submission_time ?? new Date().toISOString());

      } catch (e) {
        setError(e.message || "Failed to load report data.");
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, []);

  // ── Derived display data ───────────────────────────────────────────────────
  const dimensionsForDisplay = DIMENSION_DEFINITIONS.map(d => ({
    def: d,
    score: scores?.[d.key] ?? null,
    band: scores ? scoreBand(scores[d.key]) : null,
  }));

  const activeProfDef = PROFILE_DEFINITIONS.find(p => p.key === activeProfile);
  const participant = reportMeta?.orgName || "—";
  const assessmentDate = reportMeta?.period || "—";

  // Score ring for Page 2
  const expScore = scores?.exposure ?? 0;
  const fbScore = scores?.feedback ?? 0;
  const avgScore = scores ? ((expScore + fbScore) / 2) : 0;
  const avgPct = (avgScore / 10) * 100;
  const conicGrad = `conic-gradient(${T.navy} 0 ${avgPct.toFixed(1)}%, #dfe7ee ${avgPct.toFixed(1)}% 100%)`;

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <>
      <div className="jh-report" id="jh-report-content">
        <link href="https://api.fontshare.com/v2/css?f[]=satoshi@300,400,500,700,900&display=swap" rel="stylesheet" />
        <style>{`
          .jh-report {
            --text-xs:   clamp(0.75rem,  0.7rem  + 0.25vw, 0.875rem);
            --text-sm:   clamp(0.875rem, 0.8rem  + 0.35vw, 1rem);
            --text-base: clamp(1rem,     0.95rem + 0.25vw, 1.125rem);
            --text-lg:   clamp(1.125rem, 1rem    + 0.75vw, 1.4rem);
            --text-xl:   clamp(1.45rem,  1.2rem  + 1.1vw,  2rem);
            --space-1:.25rem; --space-2:.5rem;  --space-3:.75rem; --space-4:1rem;
            --space-5:1.25rem; --space-6:1.5rem; --space-8:2rem; --space-10:2.5rem;
            --space-12:3rem; --space-16:4rem; --space-20:5rem;
            --navy:#0f2744; --red:#A81026; --gold:#D79A1E; --ink:#1f2937; --muted:#6b7280;
            --bg:#f4f6f8; --card:#ffffff; --line:#d9e0e7; --soft:#eef2f6;
            --radius:20px; --radius-sm:12px; --shadow:0 18px 40px rgba(14,39,68,.10);
            --font-body:'Satoshi', sans-serif;

            /* Logo panel: fluid width clamped between 180px and 500px */
            --logo-w: clamp(180px, 42vw, 500px);
            --logo-h: clamp(90px,  21vw, 250px);
            --logo-img-h: clamp(30px, 6vw, 65px);
            --logo-pl: clamp(20px, 5vw, 60px);
          }
          .jh-report *, .jh-report *::before, .jh-report *::after { box-sizing:border-box; }
          .jh-report {
            font-family:var(--font-body);
            background:linear-gradient(180deg,#eef4f9 0%,#f7fafc 100%);
            color:var(--ink); line-height:1.5;
          }
          .jh-report .report { max-width:1100px; margin:0 auto; padding:var(--space-8) var(--space-4) var(--space-16); }
          .jh-report .page {
            background:var(--card); border:1px solid var(--line); border-radius:28px;
            box-shadow:var(--shadow); overflow:hidden; margin-bottom:var(--space-8);
            page-break-after:always;
          }

          /* ── Cover ── */
          .jh-report .cover {
            min-height:92vh; position:relative;
            background:radial-gradient(circle at top right,rgba(214,162,63,.22),transparent 28%),
                       linear-gradient(135deg,#A81026,#C4163A 55%,#7B0F2B);
            color:white;
          }
          .jh-report .cover-inner { padding:var(--space-16); display:flex; flex-direction:column; justify-content:space-between; min-height:92vh; }
          .jh-report .logo-panel {
            position: absolute;
            top: 0;
            right: 0;
            width: var(--logo-w);
            height: var(--logo-h);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 1;
            pointer-events: none;
          }
          .jh-report .logo-panel-inner {
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            position: relative;
            z-index: 2;
            padding-left: var(--logo-pl);
            padding-bottom: clamp(8px, 2vw, 25px);
          }
          .jh-report .logo-img {
            height: var(--logo-img-h);
            width: auto;
            object-fit: contain;
            flex-shrink: 0;
          }
          .jh-report .brand { display:flex; align-items:center; justify-content:space-between; gap:var(--space-4); position:relative; z-index:2; padding-right:clamp(0px,38vw,200px); }
          .jh-report .badge { border:1px solid rgba(255,255,255,.18); background:rgba(255,255,255,.08); padding:clamp(.3rem,.8vw,.6rem) clamp(.5rem,1.2vw,1rem); border-radius:999px; font-size:clamp(0.58rem,1.5vw,0.85rem); white-space:nowrap; flex-shrink:0; }
          .jh-report .cover-title { margin-top:var(--space-16); }
          .jh-report .cover-title h2 { margin:0 0 var(--space-4); font-size:clamp(2.6rem,5vw,4.8rem); line-height:.96; max-width:11ch; }
          .jh-report .cover-title p  { font-size:var(--text-lg); max-width:100%; color:rgba(255,255,255,.84); text-align:justify; }
          .jh-report .cover-grid { display:grid; grid-template-columns:repeat(3,1fr); gap:var(--space-4); margin-top:var(--space-12); }
          .jh-report .meta { background:rgba(255,255,255,.08); border:1px solid rgba(255,255,255,.16); border-radius:18px; padding:1rem 1.1rem; font-size:1.2rem; }
          .jh-report .meta span   { display:block; font-size:var(--text-xs); color:rgba(255,255,255,.66); margin-bottom:.25rem; }
          .jh-report .meta strong { font-size:var(--text-base); color:white; }
          .jh-report .cover-badges { display:flex; flex-wrap:wrap; gap:.6rem; margin-top:var(--space-8); }
          .jh-report .cover-badge  { background:rgba(255,255,255,.12); border:1px solid rgba(255,255,255,.18); padding:.5rem .9rem; border-radius:999px; font-size:0.95rem; }
          .jh-report .footer-note { display:flex; justify-content:space-between; gap:var(--space-4); color:rgba(255,255,255,.72); font-size:var(--text-xs); margin-top:var(--space-10); }

          /* ── Inner pages ── */
          .jh-report .page-inner { padding:var(--space-10); }
          .jh-report .topbar { display:flex; justify-content:space-between; align-items:flex-start; gap:var(--space-4); margin-bottom:var(--space-8); }
          .jh-report .section-kicker { color:var(--red); font-size:var(--text-xs); text-transform:uppercase; letter-spacing:.12em; font-weight:700; }
          .jh-report .section-title  { margin:.35rem 0 0; font-size:var(--text-xl); color:var(--navy); }
          .jh-report .summary-grid   { display:grid; grid-template-columns:1.15fr .85fr; gap:var(--space-6); }
          .jh-report .two-col        { display:grid; grid-template-columns:repeat(2,1fr); gap:var(--space-6); margin-top:var(--space-6); }
          .jh-report .profiles-layout {
  display:grid;
  grid-template-columns:1.15fr .85fr;
  gap:1.1rem;
}
          .jh-report .card { background:linear-gradient(180deg,#fff 0%,#fbfcfd 100%); border:1px solid var(--line); border-radius:var(--radius); padding:var(--space-6); }
          .jh-report .card h3 { margin:0 0 .75rem; font-size:var(--text-lg); color:var(--navy); }
          .jh-report .lead { color:#334155; font-size:var(--text-base); text-align:justify; margin:0; }

          /* Sub-panel (inside card/page for charts) */
          .jh-report .sub-panel { background:linear-gradient(180deg,#fff 0%,#fbfcfd 100%); border:1px solid var(--line); border-radius:var(--radius); padding:var(--space-5); }
          .jh-report .sub-panel h4 { margin:0 0 .75rem; font-size:var(--text-base); color:var(--navy); font-weight:700; }

          /* Score tiles */
          .jh-report .score-tile-grid { display:grid; grid-template-columns:repeat(2,1fr); gap:.8rem; margin-bottom:.85rem; }
          .jh-report .score-tile { border-radius:14px; border:1px solid var(--line); padding:.85rem; background:linear-gradient(180deg,#fff,#f8fbff); }
          .jh-report .score-tile span   { display:block; font-size:.72rem; letter-spacing:.08em; text-transform:uppercase; color:#5B677A; font-weight:700; margin-bottom:.2rem; }
          .jh-report .score-tile strong { font-size:1.85rem; color:#A81026; font-weight:700; display:block; }
          .jh-report .score-tile .band  { font-size:.72rem; font-weight:700; text-transform:uppercase; letter-spacing:.06em; margin-top:.2rem; }

          /* Score ring (Page 2) */
          .jh-report .score-wrap { display:grid; place-items:center; gap:.75rem; }
          .jh-report .score-ring { width:200px; aspect-ratio:1; border-radius:50%; display:grid; place-items:center; position:relative; }
          .jh-report .score-ring::before { content:""; position:absolute; inset:16px; background:white; border-radius:50%; box-shadow:inset 0 0 0 1px var(--line); }
          .jh-report .score-core { position:relative; text-align:center; z-index:2; }
          .jh-report .score-core strong { display:block; font-size:2.4rem; color:var(--navy); line-height:1; }
          .jh-report .score-core span   { display:block; color:var(--muted); font-size:var(--text-sm); }

          /* Pill / band */
          .jh-report .pill { display:inline-flex; align-items:center; gap:.45rem; padding:.5rem .85rem; border-radius:999px; font-size:var(--text-xs); font-weight:700; text-transform:uppercase; letter-spacing:.06em; }
          .jh-report .pill.high    { background:#fde8e8; color:#A81026; }
          .jh-report .pill.average { background:#fff7e0; color:#b07d0e; }
          .jh-report .pill.low     { background:#f1f5f9; color:#5B677A; }

          /* Bars */
          .jh-report .bars     { display:grid; gap:.5rem; }
          .jh-report .bar-row  { display:grid; grid-template-columns:180px 1fr 110px; gap:1rem; align-items:center; padding:.5rem .75rem; border-radius:12px; }
          .jh-report .bar-row.row-exposure { border-left:3px solid #A81026; }
          .jh-report .bar-row.row-feedback  { border-left:3px solid #D79A1E; }
          .jh-report .bar-label { font-size:var(--text-sm); font-weight:700; color:var(--navy); }
          .jh-report .track { height:16px; background:#e9eef3; border-radius:999px; overflow:hidden; }
          .jh-report .fill  { height:100%; border-radius:999px; transition:width .6s ease; }
          .jh-report .fill.exposure { background:linear-gradient(90deg,#A81026,#C4163A); }
          .jh-report .fill.feedback { background:linear-gradient(90deg,#b07d0e,#D79A1E); }
          .jh-report .score-box { text-align:right; font-variant-numeric:tabular-nums; }
          .jh-report .score-box strong { display:block; color:var(--navy); }
          .jh-report .score-box span   { font-size:var(--text-xs); font-weight:700; }
          .jh-report .score-box .band-high    { color:#A81026; }
          .jh-report .score-box .band-average { color:#b07d0e; }
          .jh-report .score-box .band-low     { color:#5B677A; }

          /* Callout */
          .jh-report .callout { background:linear-gradient(135deg,#fff7f7,#fbeaea); border:1px solid var(--line); border-radius:18px; padding:1rem 1.1rem; color:#334155; text-align:justify; }

          /* Profile cards (Page 3) */
          .jh-report .profiles-accordion { display:grid; gap:0; }
          .jh-report .profile-item { border-radius:16px; padding:0; margin-bottom:.85rem; overflow:hidden; }
          .jh-report .profile-item-header { display:flex; align-items:center; gap:.85rem; margin-bottom:0; width:100%; padding:1rem; background:none; border:none; text-align:left; font:inherit; cursor:pointer; }
          .jh-report .profile-item-toggle:focus-visible { outline:2px solid #A81026; outline-offset:-2px; }
          .jh-report .profile-dot { width:12px; height:12px; border-radius:50%; flex-shrink:0; }
          .jh-report .profile-meta { flex:1; min-width:0; }
          .jh-report .profile-meta strong { display:block; font-size:.97rem; }
          .jh-report .profile-meta em     { font-style:italic; font-size:.82rem; color:#5B677A; }
          .jh-report .profile-meta .thresholds { font-size:.75rem; color:#5B677A; margin-top:.15rem; }
          .jh-report .profile-chevron { font-size:1rem; font-weight:700; flex-shrink:0; transition:transform .2s ease; }
          .jh-report .profile-item-body { padding:0 1rem 1rem; }
          .jh-report .profile-item-body p { margin:0 0 .35rem; font-size:.88rem; color:#334155; text-align:justify; }
          .jh-report .your-tag { font-size:.72rem; font-weight:700; color:#D79A1E; margin-left:.5rem; }

          /* Growth lever grid */
          .jh-report .lever-grid { display:grid; grid-template-columns:1fr 1fr; gap:var(--space-4); margin-bottom:var(--space-4); }
          .jh-report .lever { border-radius:14px; padding:1rem; }

          /* List items */
          .jh-report .list { display:grid; gap:.9rem; }
          .jh-report .item { border:1px solid var(--line); border-radius:16px; padding:1rem; background:#fff; }
          .jh-report .item strong { display:block; color:var(--navy); margin-bottom:.25rem; }
          .jh-report .item p { margin:0; color:#475569; font-size:var(--text-sm); text-align:justify; }

          /* Back cover */
          .jh-report .back-cover { color:white; }
          .jh-report .back-cover-gradient {
            background:radial-gradient(circle at top right,rgba(214,162,63,.22),transparent 28%),
                       linear-gradient(135deg,#A81026,#C4163A 55%,#7B0F2B);
            min-height:56vh;
          }
          .jh-report .back-cover-header { display:flex; flex-wrap:wrap; justify-content:space-between; align-items:center; gap:.5rem; padding:clamp(.9rem,2.5vw,1.5rem) clamp(.9rem,3vw,2.5rem); border-bottom:1px solid rgba(255,255,255,.15); }
          .jh-report .back-cover .page-inner { display:flex; flex-direction:column; justify-content:space-between; }
          .jh-report .closing h2 { margin:0 0 1rem; font-size:clamp(1.4rem,4vw,3.25rem); color:#FFFFFF; }
          .jh-report .closing p  { max-width:100%; color:rgba(255,255,255,.84); font-size:clamp(.85rem,2vw,1.2rem); text-align:justify; }
          .jh-report .contact-grid { display:grid; grid-template-columns:repeat(auto-fit,minmax(min(140px,100%),1fr)); gap:clamp(.4rem,1.2vw,1rem); margin-top:var(--space-8); }
          .jh-report .contact { background:rgba(168,13,44,.1); border:1px solid rgba(255,255,255,.16); border-radius:clamp(10px,2vw,16px); padding:clamp(.6rem,1.5vw,.9rem); min-width:0; }
          .jh-report .contact span   { display:block; color:rgba(255,255,255,.64); font-size:var(--text-xs); margin-bottom:.3rem; }
          .jh-report .contact strong { font-size:clamp(.78rem,1.6vw,.92rem); word-break:break-word; }
          .jh-report .small { font-size:var(--text-xs); color:var(--muted); }
          .jh-report .light { color:rgba(255,255,255,.68); }

          @media print {
            .jh-report { background:white; }
            .jh-report .report { max-width:none; padding:0; }
            .jh-report .page { box-shadow:none; border-radius:0; margin:0 0 12px; border:1px solid #ddd; }
          }

          /* ── Tablet (≤ 768px) ── */
          @media (max-width:768px) {
            .jh-report .report { padding:1.25rem .75rem 3rem; }
            .jh-report .page { border-radius:20px; margin-bottom:1.25rem; }
            
            
            
            .jh-report .cover { min-height:88vh; }
            .jh-report .cover-inner { padding:2.25rem; min-height:88vh; }
            .jh-report .cover-title { margin-top:2.5rem; }
            .jh-report .cover-title h2 { font-size:clamp(2rem,5vw,3.2rem); }
            .jh-report .cover-grid { gap:.75rem; margin-top:1.5rem; grid-template-columns:repeat(auto-fit,minmax(min(130px,100%),1fr)); }
            .jh-report .page-inner { padding:1.75rem; }
            .jh-report .topbar { flex-wrap:wrap; gap:.5rem; }
            .jh-report .pill { font-size:10px; padding:.35rem .6rem; }
            .jh-report .summary-grid, .jh-report .two-col,.jh-report .profiles-layout {  grid-template-columns:1fr;}
            .jh-report .lever-grid { grid-template-columns:1fr; }
            .jh-report .bar-row { grid-template-columns:90px 1fr auto; gap:.5rem; }
          }

          /* ── Mobile (≤ 480px) ── */
          @media (max-width:480px) {
            .jh-report .report { padding:.75rem .5rem 2.5rem; }
            .jh-report .page { border-radius:16px; margin-bottom:.9rem; }
            
            
            
            .jh-report .cover { min-height:100svh; }
            .jh-report .cover-inner { padding:1.25rem; min-height:100svh; }
            .jh-report .cover-title { margin-top:1.5rem; }
            .jh-report .cover-title h2 { font-size:clamp(1.6rem,7vw,2.2rem); line-height:1.08; }
            .jh-report .cover-title p  { font-size:.875rem; }
            .jh-report .cover-grid { grid-template-columns:repeat(auto-fit,minmax(min(130px,100%),1fr)); gap:.5rem; margin-top:1.1rem; }
            .jh-report .meta { padding:.6rem .75rem; }
            .jh-report .cover-badges { margin-top:.9rem; gap:.4rem; }
            .jh-report .cover-badge { font-size:.7rem; padding:.3rem .55rem; }
            .jh-report .footer-note { flex-direction:column; gap:.2rem; font-size:.68rem; margin-top:1rem; }
            .jh-report .page-inner { padding:1rem; }
            .jh-report .topbar { flex-wrap:wrap; gap:.4rem; margin-bottom:1rem; }
            .jh-report .section-title { font-size:1.25rem; }
            .jh-report .pill { font-size:9px; padding:.25rem .5rem; }
            .jh-report .card { padding:.875rem; border-radius:14px; }
            .jh-report .card h3 { font-size:.95rem; margin-bottom:.45rem; }
            .jh-report .lead { font-size:.84rem; }
            .jh-report .bar-row { grid-template-columns:68px 1fr auto; gap:.4rem; }
            .jh-report .bar-label { font-size:.77rem; }
            .jh-report .track { height:14px; }
            .jh-report .score-tile-grid { grid-template-columns:1fr 1fr; }
            .jh-report .lever-grid { grid-template-columns:1fr; }
            .jh-report .contact-grid { grid-template-columns:1fr; gap:.5rem; margin-top:1rem; }
            .jh-report .closing h2 { font-size:clamp(1.45rem,5.5vw,2rem); }
            .jh-report .closing p  { font-size:.88rem; }
          }

          /* ── Tiny (≤ 420px) ── */
          @media (max-width:420px) {
            .jh-report .report { padding:.5rem .35rem 2rem; }
            .jh-report .page { border-radius:12px; margin-bottom:.75rem; }
            
            
            
            .jh-report .cover { min-height:100svh; }
            .jh-report .cover-inner { padding:1rem; min-height:100svh; }
            .jh-report .brand { padding-right:clamp(0px,44vw,160px); }
            .jh-report .badge { font-size:.55rem; padding:.25rem .5rem; }
            .jh-report .cover-title { margin-top:1rem; }
            .jh-report .cover-title h2 { font-size:clamp(1.35rem,6.5vw,1.7rem); line-height:1.1; }
            .jh-report .cover-title p  { font-size:.78rem; }
            .jh-report .cover-grid { grid-template-columns:1fr; gap:.4rem; margin-top:.9rem; }
            .jh-report .meta { padding:.5rem .65rem; border-radius:12px; }
            .jh-report .cover-badges { margin-top:.75rem; gap:.3rem; }
            .jh-report .cover-badge { font-size:.65rem; padding:.25rem .45rem; }
            .jh-report .footer-note { flex-direction:column; gap:.15rem; font-size:.62rem; margin-top:.75rem; }
            .jh-report .page-inner { padding:.85rem; }
            .jh-report .contact-grid { grid-template-columns:1fr; gap:.4rem; margin-top:.75rem; }
          }

          /* ── Download Button ── */
          #jh-pdf-btn {
            position:fixed; bottom:2rem; right:2rem; z-index:9999;
            display:inline-flex; align-items:center; gap:.6rem;
            padding:.85rem 1.6rem;
            background:linear-gradient(135deg,#d6a23f,#c8891a);
            color:white; font-family:var(--font-body);
            font-size:.9rem; font-weight:700; letter-spacing:.06em;
            border:none; border-radius:999px;
            box-shadow:0 8px 28px rgba(214,162,63,.45), 0 2px 8px rgba(0,0,0,.18);
            cursor:pointer;
            transition:transform .18s ease, box-shadow .18s ease, opacity .18s ease;
            text-transform:uppercase;
          }
          #jh-pdf-btn:hover:not(:disabled) { transform:translateY(-2px); box-shadow:0 14px 36px rgba(214,162,63,.55), 0 2px 8px rgba(0,0,0,.18); }
          #jh-pdf-btn:active:not(:disabled) { transform:translateY(0); }
          #jh-pdf-btn:disabled { opacity:.72; cursor:not-allowed; }
          #jh-pdf-btn .btn-spinner { width:16px; height:16px; border:2px solid rgba(255,255,255,.35); border-top-color:white; border-radius:50%; animation:spin .7s linear infinite; flex-shrink:0; }
          @keyframes spin { to { transform:rotate(360deg); } }
          @keyframes fadeUp { from{opacity:0;transform:translateY(18px)} to{opacity:1;transform:none} }
          .fade-up { animation:fadeUp .55s ease both; }
          @media print { #jh-pdf-btn { display:none !important; } }
        `}</style>

        {/* ── Floating PDF Button ── */}
        <button
          id="jh-pdf-btn"
          onClick={handleDownloadPDF}
          disabled={loading || isGenerating}
          aria-label="Download PDF Report"
        >
          {isGenerating ? (
            <><span className="btn-spinner" />Generating PDF…</>
          ) : (
            <>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
              Download PDF
            </>
          )}
        </button>

        <main className="report">

          {/* ══════════════════════════════════════════
              PAGE 1 — COVER
          ══════════════════════════════════════════ */}
          <section className="page cover">
            <div className="cover-inner">

              {/* White diagonal logo panel */}
              <div className="logo-panel">
                <svg
                  width="100%" height="100%"
                  viewBox="0 0 500 250"
                  style={{ position: 'absolute', top: 0, right: 0, zIndex: 0 }}
                  preserveAspectRatio="none"
                >
                  <path d="M 60,8 L 500,8 L 500,188 L 150,253 Q 120,258 113,229 Z" fill="rgba(0,0,0,0.04)" />
                  <path d="M 60,4 L 500,4 L 500,184 L 150,249 Q 120,254 113,225 Z" fill="rgba(0,0,0,0.06)" />
                  <path d="M 60,0 L 500,0 L 500,180 L 150,245 Q 120,250 113,221 Z" fill="white" />
                </svg>
                <div className="logo-panel-inner">
                  <img src={Acumen_25_Years_LOGO} alt="Acumen Logo" className="logo-img" />
                </div>
              </div>

              <div className="brand">
                <div className="badge">Confidential Individual Report</div>
              </div>

              {/* Main cover content */}
              <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", flex: 1 }}>
                <div className="cover-title">
                  <h2>Johari Window Assessment</h2>
                  <p>
                    This report presents a personalised communication and self-awareness profile
                    covering Exposure and Feedback dimensions, mapped onto the four-quadrant
                    Johari Window model — delivered in a consulting-ready visual format.
                  </p>
                </div>

                <div className="cover-grid">
                  <div className="meta"><span>Participant</span><strong>{loading ? "—" : participant}</strong></div>
                  <div className="meta"><span>Assessment Date</span><strong>{loading ? "—" : assessmentDate}</strong></div>
                  <div className="meta"><span>Profile</span><strong>{loading ? "—" : (activeProfDef?.label ?? "—")}</strong></div>
                </div>

                <div className="cover-badges">
                  {["Executive Dashboard", "Exposure & Feedback Scores", "Profile Insights", "Growth Strategy"].map(b => (
                    <span key={b} className="cover-badge">{b}</span>
                  ))}
                </div>
              </div>

              <div className="footer-note">
                <div>Based on the Johari Window framework by Joseph Luft and Harry Ingham.</div>
                <div>Each dimension scored out of 10.</div>
              </div>
            </div>
          </section>

          {/* ══════════════════════════════════════════
              PAGE 2 — EXECUTIVE DASHBOARD
          ══════════════════════════════════════════ */}
          <section className="page">
            <div className="page-inner">
              <div className="topbar">
                <div>
                  <div className="section-kicker">Page 2</div>
                  <h2 className="section-title">Executive Dashboard</h2>
                </div>
                <div className="small">Exposure · Feedback · Profile</div>
              </div>

              <p className="lead" style={{ marginBottom: "1.25rem" }}>
                This section presents your scores across the two core Johari Window dimensions and maps them onto
                the four-quadrant model. A larger Arena quadrant reflects a more open, trust-enabling communication profile.
              </p>

              {/* Johari chart + score tiles */}
              <div className="summary-grid" style={{ marginBottom: "1.25rem" }}>
                <div className="sub-panel">
                  <h4>Your Johari Window</h4>
                  <JohariWindowChart
                    exposure={scores?.exposure}
                    feedback={scores?.feedback}
                    loading={loading}
                    activeLabel={(activeProfDef?.label || "").toUpperCase()}
                  />
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(2,1fr)", gap: 8, marginTop: 14 }}>
                    {[
                      { label: "Arena", color: Q.arena, desc: "Open area" },
                      { label: "Blind", color: Q.blind, desc: "Unknown to self" },
                      { label: "Concealed", color: Q.concealed, desc: "Hidden area" },
                      { label: "Discover", color: Q.discover, desc: "Unknown to both" },
                    ].map(({ label, color, desc }) => (
                      <div key={label} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <div style={{ width: 10, height: 10, borderRadius: 2, background: color, flexShrink: 0 }} />
                        <span style={{ fontSize: ".8rem", color: T.slate }}><strong style={{ color: T.ink }}>{label}</strong> — {desc}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="sub-panel">
                  <h4>Your Dimension Scores</h4>
                  <p style={{ color: T.slate, fontSize: ".82rem", margin: "0 0 .75rem" }}>
                    Scores reflect self-reported responses on a 0–10 scale.
                  </p>
                  <div className="score-tile-grid">
                    {dimensionsForDisplay.map(({ def, score, band }) => {
                      const bandColor = band === "High" ? "#A81026" : band === "Average" ? "#b07d0e" : T.slate;
                      return (
                        <div className="score-tile" key={def.key}>
                          <span>{def.shortLabel}</span>
                          <strong>{loading ? "—" : (score ?? "—")}<span style={{ fontSize: "1rem", color: T.slate, fontWeight: 400 }}>/10</span></strong>
                          <div className="band" style={{ color: bandColor }}>{loading ? "" : band}</div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Active profile badge */}
                  {!loading && activeProfDef && (
                    <div style={{ background: `linear-gradient(135deg,${T.navy}10,${T.blue}08)`, border: `1px solid ${T.navy}30`, borderRadius: 12, padding: "10px 12px", marginBottom: ".75rem" }}>
                      <div style={{ fontSize: ".72rem", fontWeight: 700, color: T.slate, textTransform: "uppercase", letterSpacing: ".07em" }}>Your Profile</div>
                      <div style={{ fontSize: "1.35rem", fontWeight: 700, color: T.navy, marginTop: 2 }}>{activeProfDef.label}</div>
                      <div style={{ fontSize: ".8rem", color: T.slate, fontStyle: "italic" }}>{activeProfDef.tagline}</div>
                    </div>
                  )}

                  <div className="callout">
                    <strong>Evaluation insight: </strong>
                    {loading ? "Loading…" : (insightText || "Your Johari Window profile reflects your current communication strengths and areas for intentional development.")}
                  </div>
                </div>
              </div>

              {/* Dimension bars + score band ref */}
              <div className="two-col" style={{ marginTop: 0 }}>
                <div className="card">
                  <h3>Dimension Scores at a Glance <span style={{ fontSize: ".78rem", color: T.slate, fontWeight: 400 }}>— out of 10</span></h3>
                  <div className="bars">
                    {dimensionsForDisplay.map(({ def, score, band }) => (
                      <div className={`bar-row row-${def.key}`} key={def.key}>
                        <div className="bar-label">{def.shortLabel}</div>
                        <div className="track">
                          <div className={`fill ${def.key}`} style={{ width: `${((score ?? 0) / 10) * 100}%` }} />
                        </div>
                        <div className="score-box">
                          <strong>{loading ? "—" : (score ?? "—")} / 10</strong>
                          <span className={`band-${(band || "low").toLowerCase()}`}>{loading ? "" : band}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="card">
                  <h3>Score Bands &amp; Active Profile</h3>
                  {[
                    { band: "High", range: "7–10", color: "#A81026" },
                    { band: "Average", range: "4–6", color: "#b07d0e" },
                    { band: "Low", range: "0–3", color: T.slate },
                  ].map(({ band, range, color }) => (
                    <div key={band} style={{ display: "flex", justifyContent: "space-between", fontSize: ".9rem", marginBottom: ".5rem" }}>
                      <span style={{ fontWeight: 700, color }}>{band}</span>
                      <span style={{ color: T.slate }}>{range}</span>
                    </div>
                  ))}
                  <div style={{ marginTop: "1rem", padding: ".85rem", background: "#f8f4eb", borderRadius: "12px", border: `1px solid ${T.gold}44` }}>
                    <div style={{ fontSize: ".75rem", color: T.gold, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".08em", marginBottom: ".3rem" }}>
                      Active Profile
                    </div>
                    <div style={{ fontSize: ".9rem", color: T.ink }}>
                      <strong style={{ color: T.navy }}>{loading ? "—" : (activeProfDef?.label ?? "—")}</strong>
                      {activeProfDef && <> — {activeProfDef.thresholds}</>}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* ══════════════════════════════════════════
              PAGE 3 — THE FOUR JOHARI PROFILES
          ══════════════════════════════════════════ */}
          <section className="page">
            <div className="page-inner">
              <div className="topbar">
                <div>
                  <div className="section-kicker">Page 3</div>
                  <h2 className="section-title">The Four Johari Profiles</h2>
                </div>
                <div className="small">Profile mapping</div>
              </div>

              <p className="lead" style={{ marginBottom: "1.25rem" }}>
                Your Exposure and Feedback scores together determine which of the four communication profiles best describes
                your current style. Your active profile is highlighted below.
              </p>

              {/* Accordion — dominant profile first, then the rest */}
              <div className="profiles-accordion">
                {[...PROFILE_DEFINITIONS]
                  .sort((a, b) => (a.key === activeProfile ? -1 : b.key === activeProfile ? 1 : 0))
                  .map(def => {
                    const isActive = def.key === activeProfile;
                    const isOpen = (openProfile ?? activeProfile) === def.key;
                    const pc = PROFILE_COLOR[def.key] || { bg: "#f3f4f6", color: "#6b7280", border: "#d1d5db" };
                    return (
                      <div key={def.key} className="profile-item" style={{
                        borderTop: isActive ? `1.5px solid ${pc.color}` : `1px solid ${T.line}`,
                        borderRight: isActive ? `1.5px solid ${pc.color}` : `1px solid ${T.line}`,
                        borderBottom: isActive ? `1.5px solid ${pc.color}` : `1px solid ${T.line}`,
                        borderLeft: `5px solid ${pc.color}`,
                        background: isActive ? `linear-gradient(135deg,${pc.bg},#ffffff 60%)` : "#fff",
                        boxShadow: isActive ? `0 0 0 3px ${pc.color}22` : "none",
                        cursor: "pointer",
                      }}>
                        <button
                          type="button"
                          className="profile-item-header profile-item-toggle"
                          onClick={() => setOpenProfile(isOpen ? "__none__" : def.key)}
                          aria-expanded={isOpen}
                        >
                          <div className="profile-dot" style={{ background: pc.color }} />
                          <div className="profile-meta">
                            <strong style={{ color: isActive ? pc.color : T.ink }}>
                              {def.label}
                              {isActive && <span className="your-tag" style={{ color: pc.color }}>★ YOUR PROFILE</span>}
                            </strong>
                            <em>{def.tagline}</em>
                            <div className="thresholds">{def.thresholds}</div>
                          </div>
                          <div className="profile-chevron" style={{ color: pc.color, transform: isOpen ? "rotate(180deg)" : "rotate(0deg)" }}>▾</div>
                        </button>
                        {isOpen && (
                          <div className="profile-item-body">
                            <p>{def.overview}</p>
                            <p><strong>Strategy:</strong> {def.strategy}</p>
                            <p><strong>Strengths:</strong> {def.strengths}</p>
                            <p><strong>Watch out for:</strong> {def.watchOut}</p>
                          </div>
                        )}
                      </div>
                    );
                  })}

                {/* Context box */}
                <div style={{ background: "#f8f4eb", border: `1px solid ${T.gold}55`, borderRadius: "16px", padding: "1rem", marginTop: ".75rem" }}>
                  <div style={{ fontSize: ".72rem", color: T.gold, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".1em", marginBottom: ".5rem" }}>Context &amp; Meaning</div>
                  <p style={{ margin: 0, fontSize: ".88rem", color: T.ink, lineHeight: 1.6, textAlign: "justify" }}>
                    The Johari Window is a living model. Rather than fixed traits, your Arena, Blind, Concealed, and Discover
                    quadrants should shift as your career progresses, relationships deepen, and self-awareness grows.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* ══════════════════════════════════════════
              PAGE 4 — PERSONALISED INSIGHT & GROWTH
          ══════════════════════════════════════════ */}
          <section className="page">
            <div className="page-inner">
              <div className="topbar">
                <div>
                  <div className="section-kicker">Page 4</div>
                  <h2 className="section-title">Personalised Insight &amp; Growth</h2>
                </div>
                <div className="small">Tailored to your profile</div>
              </div>

              {/* Personalised insight card */}
              {activeProfDef && (
                <div className="card" style={{ marginBottom: "var(--space-6)" }}>
                  <h3>Your {activeProfDef.label} Profile ({activeProfDef.thresholds})</h3>
                  <div className="callout" style={{ marginBottom: ".85rem" }}>
                    <strong>{activeProfDef.label}: </strong>{activeProfDef.overview}
                    <br /><br />
                    <strong>Recommended strategy: </strong>{activeProfDef.strategy}
                  </div>
                  <div className="two-col" style={{ marginTop: 0 }}>
                    <div style={{ background: "#f8fbff", border: `1px solid ${T.line}`, borderRadius: 12, padding: 14 }}>
                      <div style={{ fontWeight: 700, color: T.navy, marginBottom: 6, fontSize: ".9rem" }}>✓ Strengths</div>
                      <p style={{ margin: 0, fontSize: ".87rem", color: T.ink, lineHeight: 1.55, textAlign: "justify" }}>{activeProfDef.strengths}</p>
                    </div>
                    <div style={{ background: "#fff9e8", border: `1px solid #F2D58A`, borderRadius: 12, padding: 14 }}>
                      <div style={{ fontWeight: 700, color: T.gold, marginBottom: 6, fontSize: ".9rem" }}>⚠ Watch out for</div>
                      <p style={{ margin: 0, fontSize: ".87rem", color: T.ink, lineHeight: 1.55, textAlign: "justify" }}>{activeProfDef.watchOut}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Growth levers */}
              <div className="card" style={{ marginBottom: "var(--space-6)" }}>
                <h3>Expanding Your Arena</h3>
                <p className="lead" style={{ marginBottom: "1rem" }}>
                  The Johari Window is not a fixed snapshot. The two levers below directly influence the size of your Arena quadrant:
                </p>
                <div className="lever-grid">
                  <div className="lever" style={{ background: `${T.navy}08`, border: `1px solid ${T.navy}20` }}>
                    <div style={{ fontWeight: 700, color: T.navy, marginBottom: 8 }}>↗ Increase Exposure</div>
                    <p style={{ margin: 0, fontSize: ".88rem", lineHeight: 1.55 }}>
                      Share your reasoning and context more openly. Explaining the "why" behind decisions reduces your Concealed area and builds team alignment.
                    </p>
                  </div>
                  <div className="lever" style={{ background: `${T.gold}12`, border: `1px solid ${T.gold}40` }}>
                    <div style={{ fontWeight: 700, color: T.gold, marginBottom: 8 }}>↗ Increase Feedback Receptivity</div>
                    <p style={{ margin: 0, fontSize: ".88rem", lineHeight: 1.55 }}>
                      Actively solicit input before and after key decisions. Creating safe channels for honest input reduces your Blind area.
                    </p>
                  </div>
                </div>
              </div>

              {/* Two-col: understanding + coaching note */}
              <div className="two-col" style={{ marginTop: 0 }}>
                <div className="card">
                  <h3>Understanding the Johari Window</h3>
                  <p className="lead">
                    Developed by Joseph Luft and Harry Ingham, the Johari Window maps information across two axes —
                    what is known to the self (Disclosure/Exposure) and what is known to others (Feedback).
                    Expanding the Arena through both dimensions is the primary developmental goal.
                  </p>
                </div>
                <div className="card">
                  <h3>Coaching Note</h3>
                  <p className="lead">
                    This report reflects your self-reported perceptions within your current professional context.
                    The boundaries of your Johari Window should shift as your career progresses and team dynamics evolve.
                    View these results as a roadmap for intentional growth, not a fixed assessment.
                  </p>
                </div>
              </div>

              {/* Error state */}
              {error && (
                <div style={{ background: "#fff0f0", border: "1px solid #f5c6c6", borderRadius: 12, padding: "14px 18px", color: "#9b1c1c", fontSize: ".93rem", marginTop: "1rem" }}>
                  ⚠️ {error}
                </div>
              )}
            </div>
          </section>

          {/* ══════════════════════════════════════════
              BACK COVER
          ══════════════════════════════════════════ */}
          <section className="page back-cover">
            <div className="back-cover-gradient">
              <div className="back-cover-header">
                <div style={{ fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: ".12em", fontWeight: 700, color: "rgba(255,255,255,0.66)" }}>
                  {/* Back Cover */}
                </div>
                <div style={{ fontSize: "0.72rem", color: "rgba(255,255,255,0.5)", letterSpacing: ".05em" }}>
                  Acumen Business Catalyst
                </div>
              </div>

              <div className="page-inner">
                <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", flex: 1 }}>
                  <div className="closing" style={{ padding: ".1rem" }}>
                    <h2>Building Open, Authentic Communication</h2>
                    <p>
                      The Johari Window is a living framework that expands through deliberate
                      self-disclosure and active feedback-seeking. Acumen Business Catalyst
                      uses assessments like this to turn communication data into focused
                      development conversations that drive lasting behavioural change.
                    </p>
                  </div>
                </div>

                <div>
                  <div className="card" style={{ marginTop: "1.5rem", background: "rgba(255,255,255,.06)", borderColor: "rgba(255,255,255,.14)", color: "white" }}>
                    <h3 style={{ color: "white" }}>Why Communication Transparency Matters</h3>
                    <p className="lead" style={{ color: "rgba(255,255,255,.84)" }}>
                      Research on high-performing teams consistently shows that leaders who actively expand their Arena —
                      through increased self-disclosure and deliberate feedback-seeking — create stronger psychological safety,
                      faster trust cycles, and more productive team dynamics than those who remain in guarded or
                      one-directional communication patterns.
                    </p>
                  </div>

                  <div className="contact-grid">
                    <div className="contact"><span>Brand</span><strong>Acumen Business Catalyst</strong></div>
                    <div className="contact"><span>Focus</span><strong>Leadership • Culture • Capability</strong></div>
                    <div className="contact"><span>Report type</span><strong>Individual Communication Profile</strong></div>
                  </div>

                  <p className="light" style={{ marginTop: "1.5rem", fontSize: "0.75rem" }}>
                    This report is generated from the Johari Window (JH) assessment framework. © 2026 Acumen Business Catalyst.
                  </p>
                </div>
              </div>
            </div>
          </section>

        </main>
        
      </div>

      {/* Hidden PDF generator — rendered OUTSIDE .jh-report to prevent CSS cascade conflicts */}
      {pdfData && (
        <HiddenJHReport
          participantData={pdfData}
          onComplete={handlePdfComplete}
        />
      )}
    </>
  );
}